import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControlLabel,
  Switch,
  Tabs,
  Tab,
  Box,
  Typography,
  IconButton,
  Chip,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  Paper,
  Tooltip,
  Alert,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Functions as FunctionsIcon,
  FilterAlt as FilterIcon,
  HelpOutline as HelpIcon,
} from '@mui/icons-material';
import { ProcessNode, CalculatedColumn } from '../../types/report';
import { FilterGroup, FilterCondition } from '../EmployeeFilter';

interface ProcessConfigModalProps {
  open: boolean;
  node: ProcessNode | null;
  parentId: string;
  parentName?: string;
  onSave: (node: ProcessNode) => void;
  onClose: () => void;
}

const DEFAULT_MAIN_FIELDS = [
  'internalId',
  'name',
  'department',
  'designation',
  'joiningDate',
  'resignationDate',
  'employeeType',
  'branch',
];

const DEFAULT_DETAILS_FIELDS = [
  'Date',
  'inTime',
  'outTime',
  'isLate',
  'LateMinute',
  'isAbsence',
  'isOnLeave',
  'LeaveType',
  'PunchError',
  'isPresentInHoliday',
  'isHoliday',
  'isWeeklyOff',
  'shiftName',
  'workStartTime',
  'workEndTime',
];

const OPERATORS = [
  { value: 'equals', label: 'Equals (=)' },
  { value: 'not_equals', label: 'Not Equals (!=)' },
  { value: 'contains', label: 'Contains' },
  { value: 'not_contains', label: 'Not Contains' },
  { value: 'starts_with', label: 'Starts With' },
  { value: 'ends_with', label: 'Ends With' },
  { value: 'has_value', label: 'Has Value (Not Empty)' },
  { value: 'is_empty', label: 'Is Empty' },
  { value: 'gt', label: 'Greater Than (>)' },
  { value: 'gte', label: 'Greater Than / Equal (>=)' },
  { value: 'lt', label: 'Less Than (<)' },
  { value: 'lte', label: 'Less Than / Equal (<=)' },
];

export const ProcessConfigModal: React.FC<ProcessConfigModalProps> = ({
  open,
  node,
  parentId,
  parentName,
  onSave,
  onClose,
}) => {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [keepEmptyEmployees, setKeepEmptyEmployees] = useState(false);
  const [mainTab, setMainTab] = useState(0); // 0 = Filters, 1 = Calculations
  const [filterSubTab, setFilterSubTab] = useState(0); // 0 = Main Filters, 1 = Details Filters
  const [calcSubTab, setCalcSubTab] = useState(0); // 0 = Main Calculations, 1 = Details Calculations

  const [mainFilters, setMainFilters] = useState<FilterGroup[]>([]);
  const [detailsFilters, setDetailsFilters] = useState<FilterGroup[]>([]);
  const [mainCalculations, setMainCalculations] = useState<CalculatedColumn[]>([]);
  const [detailsCalculations, setDetailsCalculations] = useState<CalculatedColumn[]>([]);

  const [formError, setFormError] = useState<string | null>(null);

  // Initialize or reset form on node change
  useEffect(() => {
    if (open) {
      if (node) {
        setName(node.name || '');
        setDescription(node.description || '');
        setKeepEmptyEmployees(!!node.keepEmptyEmployees);
        setMainFilters(node.mainFilters || []);
        setDetailsFilters(node.detailsFilters || []);
        setMainCalculations(node.mainCalculations || []);
        setDetailsCalculations(node.detailsCalculations || []);
      } else {
        setName('');
        setDescription('');
        setKeepEmptyEmployees(false);
        setMainFilters([]);
        setDetailsFilters([]);
        setMainCalculations([]);
        setDetailsCalculations([]);
      }
      setMainTab(0);
      setFilterSubTab(0);
      setCalcSubTab(0);
      setFormError(null);
    }
  }, [open, node]);

  // Filter group helpers
  const handleAddFilterGroup = (isDetails: boolean) => {
    const newGroup: FilterGroup = {
      id: 'grp_' + Date.now() + Math.random().toString(36).substring(2, 6),
      join: 'AND',
      logic: 'AND',
      conditions: [
        {
          id: 'c_' + Date.now() + Math.random().toString(36).substring(2, 6),
          fieldName: isDetails ? DEFAULT_DETAILS_FIELDS[0] : DEFAULT_MAIN_FIELDS[0],
          operator: 'equals',
          value: '',
          enabled: true,
        },
      ],
    };

    if (isDetails) {
      setDetailsFilters((prev) => [...prev, newGroup]);
    } else {
      setMainFilters((prev) => [...prev, newGroup]);
    }
  };

  const handleAddCondition = (groupId: string, isDetails: boolean) => {
    const target = isDetails ? detailsFilters : mainFilters;
    const updated = target.map((g) => {
      if (g.id !== groupId) return g;
      const newCond: FilterCondition = {
        id: 'c_' + Date.now() + Math.random().toString(36).substring(2, 6),
        fieldName: isDetails ? DEFAULT_DETAILS_FIELDS[0] : DEFAULT_MAIN_FIELDS[0],
        operator: 'equals',
        value: '',
        enabled: true,
      };
      return { ...g, conditions: [...g.conditions, newCond] };
    });
    if (isDetails) setDetailsFilters(updated);
    else setMainFilters(updated);
  };

  const handleRemoveCondition = (groupId: string, condId: string, isDetails: boolean) => {
    const target = isDetails ? detailsFilters : mainFilters;
    const updated = target
      .map((g) => {
        if (g.id !== groupId) return g;
        return { ...g, conditions: g.conditions.filter((c) => c.id !== condId) };
      })
      .filter((g) => g.conditions.length > 0);
    if (isDetails) setDetailsFilters(updated);
    else setMainFilters(updated);
  };

  const handleUpdateCondition = (
    groupId: string,
    condId: string,
    field: keyof FilterCondition,
    val: any,
    isDetails: boolean
  ) => {
    const target = isDetails ? detailsFilters : mainFilters;
    const updated = target.map((g) => {
      if (g.id !== groupId) return g;
      return {
        ...g,
        conditions: g.conditions.map((c) => (c.id === condId ? { ...c, [field]: val } : c)),
      };
    });
    if (isDetails) setDetailsFilters(updated);
    else setMainFilters(updated);
  };

  // Calculations helpers
  const handleAddCalculation = (isDetails: boolean) => {
    const newCol: CalculatedColumn = {
      name: isDetails ? 'customDetail' + (detailsCalculations.length + 1) : 'customMain' + (mainCalculations.length + 1),
      formula: '',
      dataType: 'number',
    };
    if (isDetails) {
      setDetailsCalculations((prev) => [...prev, newCol]);
    } else {
      setMainCalculations((prev) => [...prev, newCol]);
    }
  };

  const handleUpdateCalculation = (index: number, field: keyof CalculatedColumn, val: any, isDetails: boolean) => {
    if (isDetails) {
      setDetailsCalculations((prev) => {
        const next = [...prev];
        next[index] = { ...next[index], [field]: val };
        return next;
      });
    } else {
      setMainCalculations((prev) => {
        const next = [...prev];
        next[index] = { ...next[index], [field]: val };
        return next;
      });
    }
  };

  const handleRemoveCalculation = (index: number, isDetails: boolean) => {
    if (isDetails) {
      setDetailsCalculations((prev) => prev.filter((_, i) => i !== index));
    } else {
      setMainCalculations((prev) => prev.filter((_, i) => i !== index));
    }
  };

  const insertSnippet = (index: number, snippet: string, isDetails: boolean) => {
    if (isDetails) {
      setDetailsCalculations((prev) => {
        const next = [...prev];
        const currentFormula = next[index]?.formula || '';
        next[index] = { ...next[index], formula: currentFormula + snippet };
        return next;
      });
    } else {
      setMainCalculations((prev) => {
        const next = [...prev];
        const currentFormula = next[index]?.formula || '';
        next[index] = { ...next[index], formula: currentFormula + snippet };
        return next;
      });
    }
  };

  const handleSave = () => {
    if (!name.trim()) {
      setFormError('Process name is required.');
      return;
    }

    // Validate calculated column names
    const checkNames = (cols: CalculatedColumn[]) => {
      const names = new Set<string>();
      for (const col of cols) {
        const cName = col.name.trim();
        if (!cName) return 'Calculated column name cannot be empty.';
        if (!/^[a-zA-Z_][a-zA-Z0-9_]*$/.test(cName)) {
          return `Column name "${cName}" must contain only letters, numbers, and underscores.`;
        }
        if (names.has(cName)) {
          return `Duplicate calculated column name: "${cName}".`;
        }
        names.add(cName);
      }
      return null;
    };

    const mainErr = checkNames(mainCalculations);
    if (mainErr) {
      setFormError(mainErr);
      return;
    }
    const detailsErr = checkNames(detailsCalculations);
    if (detailsErr) {
      setFormError(detailsErr);
      return;
    }

    const updatedNode: ProcessNode = {
      id: node ? node.id : 'proc_' + Date.now().toString(36),
      parentId: node ? node.parentId : parentId,
      name: name.trim(),
      description: description.trim(),
      keepEmptyEmployees,
      mainFilters,
      detailsFilters,
      mainCalculations,
      detailsCalculations,
    };

    onSave(updatedNode);
  };

  const renderFilterSection = (isDetails: boolean) => {
    const groups = isDetails ? detailsFilters : mainFilters;
    const defaultFields = isDetails ? DEFAULT_DETAILS_FIELDS : DEFAULT_MAIN_FIELDS;

    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="subtitle2" color="text.secondary">
            {isDetails
              ? 'Filter daily attendance records. Filtered-out dates are pruned from the Details array.'
              : 'Filter employee records. Excludes non-matching employees from this process.'}
          </Typography>
          <Button
            size="small"
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={() => handleAddFilterGroup(isDetails)}
          >
            Add Filter Group
          </Button>
        </Box>

        {groups.length === 0 ? (
          <Paper variant="outlined" sx={{ p: 3, textAlign: 'center', bgcolor: 'grey.50' }}>
            <Typography variant="body2" color="text.secondary">
              No filters defined. All records from parent will be passed through.
            </Typography>
          </Paper>
        ) : (
          groups.map((group, gIdx) => (
            <Paper key={group.id} variant="outlined" sx={{ p: 2, bgcolor: 'background.paper', borderRadius: 1.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {gIdx > 0 && (
                    <Select
                      size="small"
                      value={group.join}
                      onChange={(e) => {
                        const target = isDetails ? detailsFilters : mainFilters;
                        const updated = target.map((g) => (g.id === group.id ? { ...g, join: e.target.value as any } : g));
                        if (isDetails) setDetailsFilters(updated);
                        else setMainFilters(updated);
                      }}
                      sx={{ minWidth: 80, height: 28, fontSize: '0.75rem', fontWeight: 600 }}
                    >
                      <MenuItem value="AND">AND</MenuItem>
                      <MenuItem value="OR">OR</MenuItem>
                    </Select>
                  )}
                  <Typography variant="body2" fontWeight={600} color="primary.main">
                    Group {gIdx + 1}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    (Match
                  </Typography>
                  <Select
                    size="small"
                    value={group.logic}
                    onChange={(e) => {
                      const target = isDetails ? detailsFilters : mainFilters;
                      const updated = target.map((g) => (g.id === group.id ? { ...g, logic: e.target.value as any } : g));
                      if (isDetails) setDetailsFilters(updated);
                      else setMainFilters(updated);
                    }}
                    sx={{ minWidth: 70, height: 26, fontSize: '0.75rem' }}
                  >
                    <MenuItem value="AND">ALL</MenuItem>
                    <MenuItem value="OR">ANY</MenuItem>
                  </Select>
                  <Typography variant="caption" color="text.secondary">
                    conditions)
                  </Typography>
                </Box>
                <Button
                  size="small"
                  color="error"
                  onClick={() => {
                    const target = isDetails ? detailsFilters : mainFilters;
                    const updated = target.filter((g) => g.id !== group.id);
                    if (isDetails) setDetailsFilters(updated);
                    else setMainFilters(updated);
                  }}
                  sx={{ fontSize: '0.75rem' }}
                >
                  Remove Group
                </Button>
              </Box>

              {group.conditions.map((cond) => (
                <Box key={cond.id} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <TextField
                    size="small"
                    placeholder="Field name"
                    value={cond.fieldName}
                    onChange={(e) => handleUpdateCondition(group.id, cond.id, 'fieldName', e.target.value, isDetails)}
                    sx={{ width: 180, '& .MuiInputBase-input': { fontSize: '0.8rem', py: 0.75 } }}
                    helperText={!defaultFields.includes(cond.fieldName) ? 'Custom / dynamic field' : undefined}
                  />
                  <Select
                    size="small"
                    value={cond.operator}
                    onChange={(e) => handleUpdateCondition(group.id, cond.id, 'operator', e.target.value, isDetails)}
                    sx={{ width: 170, height: 34, fontSize: '0.8rem' }}
                  >
                    {OPERATORS.map((op) => (
                      <MenuItem key={op.value} value={op.value}>
                        {op.label}
                      </MenuItem>
                    ))}
                  </Select>
                  <TextField
                    size="small"
                    placeholder="Value"
                    value={cond.value}
                    disabled={cond.operator === 'has_value' || cond.operator === 'is_empty'}
                    onChange={(e) => handleUpdateCondition(group.id, cond.id, 'value', e.target.value, isDetails)}
                    sx={{ flexGrow: 1, '& .MuiInputBase-input': { fontSize: '0.8rem', py: 0.75 } }}
                  />
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() => handleRemoveCondition(group.id, cond.id, isDetails)}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Box>
              ))}

              <Box sx={{ display: 'flex', justifyContent: 'flex-start', mt: 1 }}>
                <Button
                  size="small"
                  startIcon={<AddIcon fontSize="small" />}
                  onClick={() => handleAddCondition(group.id, isDetails)}
                  sx={{ fontSize: '0.75rem' }}
                >
                  Add Condition
                </Button>
              </Box>
            </Paper>
          ))
        )}
      </Box>
    );
  };

  const renderCalculationSection = (isDetails: boolean) => {
    const list = isDetails ? detailsCalculations : mainCalculations;

    const mainSnippets = [
      { label: 'COUNT inTime', code: 'COUNT(Details.inTime)' },
      { label: 'SUM LateMin', code: 'SUM(Details.LateMinute)' },
      { label: 'AVG LateMin', code: 'AVG(Details.LateMinute)' },
      { label: 'FIRST inTime', code: 'FIRST(Details.inTime)' },
      { label: 'LAST outTime', code: 'LAST(Details.outTime)' },
      { label: 'EXACT_DATE', code: "EXACT_DATE(Details.inTime, '2026-10-01')" },
      { label: 'IF(...)', code: 'IF(SUM(Details.LateMinute) > 60, "Warning", "Good")' },
    ];

    const detailSnippets = [
      { label: 'TIME_DIFF (min)', code: "TIME_DIFF(Details.outTime, Details.inTime, 'm')" },
      { label: 'TIME_DIFF (hr)', code: "TIME_DIFF(Details.outTime, Details.inTime, 'h')" },
      { label: 'DATE_DIFF (days)', code: "DATE_DIFF(Details.Date, Main.joiningDate, 'd')" },
      { label: 'IF isLateNew', code: 'IF(Details.LateMinute > 15, true, false)' },
      { label: 'Main.internalId', code: 'Main.internalId' },
      { label: 'Details.LateMinute', code: 'Details.LateMinute' },
    ];

    const snippets = isDetails ? detailSnippets : mainSnippets;

    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="subtitle2" color="text.secondary">
            {isDetails
              ? 'Row-by-row calculated columns evaluated on each daily Details record (e.g. isLateNew).'
              : 'Main employee attributes calculated across whole employee or aggregated over Details (e.g. SUM, COUNT).'}
          </Typography>
          <Button
            size="small"
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={() => handleAddCalculation(isDetails)}
          >
            Add Column
          </Button>
        </Box>

        {list.length === 0 ? (
          <Paper variant="outlined" sx={{ p: 3, textAlign: 'center', bgcolor: 'grey.50' }}>
            <Typography variant="body2" color="text.secondary">
              No calculated columns added. Click &quot;Add Column&quot; to define derived metrics.
            </Typography>
          </Paper>
        ) : (
          list.map((col, idx) => (
            <Paper key={idx} variant="outlined" sx={{ p: 2, bgcolor: 'background.paper', borderRadius: 1.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                <TextField
                  size="small"
                  label="Column Name"
                  value={col.name}
                  onChange={(e) => handleUpdateCalculation(idx, 'name', e.target.value, isDetails)}
                  sx={{ width: 180, '& .MuiInputBase-input': { fontSize: '0.85rem' } }}
                />
                <FormControl size="small" sx={{ width: 130 }}>
                  <InputLabel>Type</InputLabel>
                  <Select
                    value={col.dataType}
                    label="Type"
                    onChange={(e) => handleUpdateCalculation(idx, 'dataType', e.target.value, isDetails)}
                    sx={{ fontSize: '0.85rem' }}
                  >
                    <MenuItem value="number">Number</MenuItem>
                    <MenuItem value="string">String</MenuItem>
                    <MenuItem value="boolean">Boolean</MenuItem>
                  </Select>
                </FormControl>
                <TextField
                  size="small"
                  label="Formula Expression"
                  fullWidth
                  value={col.formula}
                  placeholder={
                    isDetails
                      ? 'e.g. IF(Details.LateMinute > 15, true, false)'
                      : 'e.g. SUM(Details.LateMinute) / 60'
                  }
                  onChange={(e) => handleUpdateCalculation(idx, 'formula', e.target.value, isDetails)}
                  sx={{
                    flexGrow: 1,
                    '& .MuiInputBase-input': {
                      fontFamily: 'monospace',
                      fontSize: '0.85rem',
                      color: 'primary.dark',
                    },
                  }}
                />
                <IconButton
                  size="small"
                  color="error"
                  onClick={() => handleRemoveCalculation(idx, isDetails)}
                  sx={{ mt: 0.5 }}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Box>

              {/* Quick snippet inserter */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap', mt: 1.5, pl: 0.5 }}>
                <Typography variant="caption" color="text.secondary" fontWeight={500}>
                  Insert Snippet:
                </Typography>
                {snippets.map((snip) => (
                  <Chip
                    key={snip.label}
                    label={snip.label}
                    size="small"
                    variant="outlined"
                    onClick={() => insertSnippet(idx, snip.code, isDetails)}
                    sx={{
                      cursor: 'pointer',
                      fontSize: '0.72rem',
                      fontFamily: 'monospace',
                      '&:hover': { bgcolor: 'action.hover' },
                    }}
                  />
                ))}
              </Box>
            </Paper>
          ))
        )}
      </Box>
    );
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ pb: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box>
          <Typography variant="h6" fontWeight={600}>
            {node ? `Edit Process: ${node.name}` : 'Configure New Child Process'}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Parent Node: <strong>{parentName || (parentId === 'base-data' ? 'Base Data (Root)' : parentId)}</strong>
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent dividers sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, minHeight: 450 }}>
        {formError && <Alert severity="error">{formError}</Alert>}

        {/* Basic Information */}
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <TextField
            size="small"
            required
            label="Process Name"
            placeholder="e.g. Factory Overtime Audit"
            value={name}
            onChange={(e) => setName(e.target.value)}
            sx={{ flex: 1.5 }}
          />
          <TextField
            size="small"
            label="Description (Optional)"
            placeholder="e.g. Filters late employees and adds overtime metrics"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            sx={{ flex: 2 }}
          />
          <FormControlLabel
            control={
              <Switch
                checked={keepEmptyEmployees}
                onChange={(e) => setKeepEmptyEmployees(e.target.checked)}
                size="small"
              />
            }
            label={
              <Tooltip title="If checked, employees are kept in the report even if all daily attendance records are pruned by Details filters.">
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Typography variant="body2">Keep Empty Employees</Typography>
                  <HelpIcon fontSize="inherit" color="action" />
                </Box>
              </Tooltip>
            }
            sx={{ flexShrink: 0 }}
          />
        </Box>

        {/* Main Tab Bar */}
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={mainTab} onChange={(_, val) => setMainTab(val)}>
            <Tab icon={<FilterIcon sx={{ fontSize: 18 }} />} iconPosition="start" label="Filter Rules" />
            <Tab icon={<FunctionsIcon sx={{ fontSize: 18 }} />} iconPosition="start" label="Calculated Columns" />
          </Tabs>
        </Box>

        {/* Tab 0: Filters */}
        {mainTab === 0 && (
          <Box>
            <Tabs
              value={filterSubTab}
              onChange={(_, val) => setFilterSubTab(val)}
              sx={{ minHeight: 36, mb: 1 }}
            >
              <Tab label="Main Filters (Employee Level)" sx={{ minHeight: 36, py: 0.5, fontSize: '0.82rem' }} />
              <Tab label="Details Filters (Daily Records)" sx={{ minHeight: 36, py: 0.5, fontSize: '0.82rem' }} />
            </Tabs>
            {filterSubTab === 0 && renderFilterSection(false)}
            {filterSubTab === 1 && renderFilterSection(true)}
          </Box>
        )}

        {/* Tab 1: Calculated Columns */}
        {mainTab === 1 && (
          <Box>
            <Tabs
              value={calcSubTab}
              onChange={(_, val) => setCalcSubTab(val)}
              sx={{ minHeight: 36, mb: 1 }}
            >
              <Tab label="Main Calculations (Aggregations)" sx={{ minHeight: 36, py: 0.5, fontSize: '0.82rem' }} />
              <Tab label="Details Calculations (Row Expressions)" sx={{ minHeight: 36, py: 0.5, fontSize: '0.82rem' }} />
            </Tabs>
            {calcSubTab === 0 && renderCalculationSection(false)}
            {calcSubTab === 1 && renderCalculationSection(true)}
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} color="inherit">
          Cancel
        </Button>
        <Button onClick={handleSave} variant="contained" color="primary">
          Save Configuration
        </Button>
      </DialogActions>
    </Dialog>
  );
};
